import {
  CascadiaColor,
  CascadiaStandardIcon,
  CascadiaTypographyStyle,
} from "@certn/cascadia-tokens";
import { css, cx } from "@emotion/css";

import { Text } from "../../../foundations/typography/Text";
import { GhostIconButton } from "../../button/GhostIconButton";
import { getInnerContentTextStyles } from "./utils";
import { LabelAlign } from "../../types";

export interface InnerContentItemProps {
  children?: React.ReactNode;
  label?: string;
  icon?: CascadiaStandardIcon;
  provided?: boolean;
  onIconPress?: () => void;
  testID?: string;
  textStyle?: CascadiaTypographyStyle;
  textColor?: CascadiaColor;
  labelAlign?: LabelAlign;
}

export function InnerContentItem({
  label,
  children,
  icon,
  provided = true,
  onIconPress: onIconClick,
  testID = "inner-content-item",
  textStyle,
  textColor,
  labelAlign = "vertical",
}: InnerContentItemProps) {
  const textStyles = getInnerContentTextStyles(
    labelAlign,
    textStyle,
    textColor
  );

  return (
    <div
      className={cx(
        "InnerContentItem",
        css([
          styles.innerContentItem,
          labelAlign === "horizontal" && styles.horizontalSpacing,
        ])
      )}
      data-testid={`${testID}-inner-content`}
    >
      <div className={css(styles.innerContentTextWrapper)}>
        <div
          className={css(
            styles.content,
            labelAlign === "horizontal" && styles.horizontalAlign
          )}
        >
          {label && (
            <Text
              as="div"
              style={textStyles.label.style}
              color={textStyles.label.color}
              testID={`${testID}-label`}
            >
              {label}
            </Text>
          )}
          {typeof children === "string" ? (
            <Text
              style={textStyles.value.style}
              color={provided ? textStyles.value.color : "text-placeholder"}
              testID={`${testID}-content`}
            >
              {children}
            </Text>
          ) : (
            children
          )}
        </div>
        {icon && (
          <GhostIconButton size="small" onPress={onIconClick} icon={icon} />
        )}
      </div>
    </div>
  );
}

const styles = {
  innerContentTextWrapper: css({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-end",
  }),
  horizontalAlign: css({
    display: "flex",
    flexDirection: "row",
    "> :first-child": {
      width: 160,
    },
  }),
  innerContentItem: css({
    marginTop: "var(--space-4)",
    marginBottom: "var(--space-4)",
    paddingRight: "var(--space-4)",
    paddingLeft: "var(--space-4)",
  }),
  horizontalSpacing: css({
    paddingRight: "var(--space-0)",
    paddingLeft: "var(--space-0)",
  }),
  content: css({ flex: 1 }),
};
